import { Box, Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCreatePassword } from '../../../hooks/queries/useCreatePassword';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import PasswordField from '../../../components/shared/PasswordField';
import LoginRegistrationHeader from '../../../components/layout/LoginRegistrationHeader';

const validationSchema = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .min(12, 'Password must be at least 12 characters')
    .matches(/[!@#$%^&*]/, 'Password must contain a special character'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
});

function EnterPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { isPending, sendSetPasswordRequest, sendResetPasswordRequest } =
    useCreatePassword();

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    isValid,
  } = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: (data: { password: string; confirmPassword: string }) => {
      const { password } = data || {};

      if (token) {
        sendResetPasswordRequest(token, password);
      } else {
        sendSetPasswordRequest(password);
      }
    },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const password = values?.password || '';

  // Validation checks for each rule
  const isMinLengthValid = password?.length >= 12;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasSpecialChar = /[\W_]/.test(password);

  const getBullet = (isValid: boolean) => {
    return (
      <span
        style={{
          display: 'inline-block',
          marginRight: '8px',
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          backgroundColor: isValid ? 'purple' : 'black',
        }}
      />
    );
  };

  return (
    <form className="d-flex flex-column flex-1" onSubmit={handleSubmit}>
      <LoginRegistrationHeader />

      <Box pt={5} mb={5}>
        <Typography variant="h4" color="primary" mb={1.5}>
          {token ? 'Create a new password' : 'Create your password'}
        </Typography>
        <Typography variant="body1">
          {token
            ? 'Make sure it’s different from your previous one.'
            : 'Create a unique password to access the platform'}
        </Typography>
      </Box>
      <Box className="d-flex flex-column" gap={3}>
        <PasswordField
          name="password"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.password}
          placeholder={token ? 'New password' : 'Password'}
          fullWidth
          error={Boolean(touched.password && errors.password)}
        />
        <PasswordField
          name="confirmPassword"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.confirmPassword}
          placeholder="Confirm password"
          fullWidth
          error={Boolean(touched.confirmPassword && errors.confirmPassword)}
          helperText={touched.confirmPassword && errors.confirmPassword}
        />
      </Box>
      <Box pt={5} mb={5}>
        <Typography
          display="flex"
          alignItems="center"
          variant="body2"
          style={{ color: isMinLengthValid ? '#7625DC' : 'black' }}
        >
          {getBullet(isMinLengthValid)} Minimum 12 characters
        </Typography>
        <Typography
          display="flex"
          alignItems="center"
          variant="body2"
          style={{ color: hasUpperCase ? '#7625DC' : 'black' }}
        >
          {getBullet(hasUpperCase)} Must contain at least one uppercase letter
        </Typography>
        <Typography
          display="flex"
          alignItems="center"
          variant="body2"
          style={{ color: hasLowerCase ? '#7625DC' : 'black' }}
        >
          {getBullet(hasLowerCase)} Must contain at least one lowercase letter
        </Typography>
        <Typography
          display="flex"
          alignItems="center"
          variant="body2"
          style={{ color: hasSpecialChar ? '#7625DC' : 'black' }}
        >
          {getBullet(hasSpecialChar)} Must contain at least one special
          character
        </Typography>
      </Box>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 'auto', mb: 4.5 }}
        disabled={!isValid || isPending}
      >
        {token ? 'Create password' : 'Confirm'}
      </Button>
    </form>
  );
}

export default EnterPassword;
